/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import { endpoints } from '@ibm-watson/shared/modules/ia/util/config';
import { validation } from '@ibm-watson/shared/modules/ia/util/validation';
import { getAccessPrefixedUrl } from '../../../../ia/util/access-control';

const valuesURL = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entityId: string
) =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/entities/${entityId}/values`
    )
  );

const valueURL = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entityId: string,
  value: any
) =>
  `${valuesURL(
    skillId,
    useNewIAUpdatedAccess,
    assistantId,
    entityId
  )}/${encodeURIComponent(value)}`;

const validate = (skillId: string, value: any = null) => {
  const errors = [];
  const guidValidationErrors = validation.validateGuid(skillId);

  if (guidValidationErrors) {
    errors.push(guidValidationErrors);
  }

  if (value) {
    const valueValidationErrors = validation.validateValue(value);
    if (valueValidationErrors) {
      errors.push(valueValidationErrors);
    }
  }

  if (errors.length) {
    return Promise.reject({ data: { error: errors } });
  }
  return null;
};

export const create = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entityId: string,
  value: any
) => {
  const validationErrors = validate(skillId, value);
  if (validationErrors) {
    return validationErrors;
  }

  return axios
    .post(
      valuesURL(skillId, useNewIAUpdatedAccess, assistantId, entityId),
      value
    )
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

export const get = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entityId: string,
  valueName: string
) => {
  const validationErrors = validate(skillId);
  if (validationErrors) {
    return validationErrors;
  }
  const config = {
    params: { export: true }
  };
  return axios
    .get(
      valueURL(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        entityId,
        valueName
      ),
      config
    )
    .then(response => response.data);
};

export const update = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  entityId: string,
  previousValue: any,
  updatedValue: any
) => {
  const validationErrors = validate(skillId, updatedValue);
  if (validationErrors) {
    return validationErrors;
  }

  return axios
    .post(
      valueURL(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        entityId,
        previousValue
      ),
      updatedValue
    )
    .then(response => {
      const updatedValue = response.data;
      return get(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        entityId,
        updatedValue.value
      );
    })
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};
